import * as React from 'react';

import { Icon } from '@components/Icon';

import { ToggleButton } from './ToggleButton';

import { LinkWithState as LocalizedLink } from '@utils/localized-link';

import { useTranslation } from '@external/react-i18next';

interface Props {
  title: string;
  items?:
    | {
        id: number;
        label: string;
        path: string;
        handleClick?: () => void;
        isDisabled?: boolean;
      }[]
    | null;
  className?: string;
  passThrough?: object;
  optionsToggleButton?: boolean;
  isRightIndent?: boolean;
  customToggleButton?: React.ReactElement;
  customContent?: React.ReactElement | null;
  classesMenuContainer?: string;
}

export const ContextMenu: React.FC<Props> = ({
  title,
  items,
  className = '',
  passThrough,
  optionsToggleButton = false,
  isRightIndent = true,
  customToggleButton,
  customContent = null,
  classesMenuContainer = '',
}) => {
  const menuElement = React.useRef<HTMLDivElement>(null);
  const toggleButton = React.useRef<HTMLButtonElement>(null);
  const { t } = useTranslation();

  const handleDocumentClick = React.useCallback(
    (e: MouseEvent): void => {
      if (
        !menuElement?.current?.contains(e.target as Node) &&
        !toggleButton?.current?.contains(e.target as Node)
      ) {
        menuElement?.current?.classList.add('hidden');
        document.removeEventListener('mousedown', handleDocumentClick);
      }
    },
    [menuElement]
  );

  const toggleContextMenu = () => {
    menuElement?.current?.classList.toggle('hidden');
    if (!menuElement?.current?.classList.contains('hidden')) {
      document.addEventListener('mousedown', handleDocumentClick);
    }
  };

  React.useEffect(() => {
    return () => {
      document.removeEventListener('mousedown', handleDocumentClick);
    };
  }, [handleDocumentClick]);

  return (
    <div
      data-menu-component
      className={`relative ml-auto mr-${isRightIndent ? 5 : 0} ${className}`}
    >
      <ToggleButton
        customToggleButton={customToggleButton}
        toggleMenuClick={toggleContextMenu}
        toggleRef={toggleButton}
        optionsButton={optionsToggleButton}
        titleText={title}
      />
      {(items?.length || customContent) && (
        <div className="absolute right-0">
          <div
            role="menu"
            data-menu-origin="left"
            className={`hidden context-menu desktop-context-menu ${classesMenuContainer}`}
            ref={menuElement}
          >
            <div className="desktop:hidden px-5 pt-8 border-grey-300 border-b">
              <button
                data-testid="close-menu-btn"
                type="button"
                className="block ml-auto"
                aria-haspopup="true"
                aria-expanded="false"
                onClick={toggleContextMenu}
              >
                <span aria-hidden="true">
                  <Icon name="ui/close" size="17" color="black" />
                </span>
                <span className="hidden">
                  {t('context-menu.close', 'Close context menu')}
                </span>
              </button>
              <h3 className="text-lg font-bold text-gray-600 mb-4">{title}</h3>
            </div>
            <ul
              data-label={t(
                'context-menu.label',
                'Context menu for {{title}}',
                {
                  title,
                }
              )}
            >
              {customContent}
              {items?.map((item, key) => {
                const contextMenuKey = `${key}-context-menu-item`;
                return (
                  <li key={contextMenuKey}>
                    {item.isDisabled ? (
                      <span className="context-menu-item-disabled">
                        {item.label}
                      </span>
                    ) : (
                      <LocalizedLink
                        onClick={e => {
                          if (item.handleClick) {
                            e.preventDefault();
                            item.handleClick();
                          }
                        }}
                        to={item.path}
                        state={passThrough}
                        className="context-menu-item"
                        activeClassName="context-menu-item-active"
                        dangerouslySetInnerHTML={{ __html: item.label }}
                      />
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};
